import { integrationsInstance } from '@/shared/api';

export type AmoCrmOAuthResult = {
  integra_id: string;
};

type AmoCrmOAuthStartResponse = {
  state?: string;
  authUrl?: string;
};

type AmoCrmOAuthExchangeResponse = {
  integra_id?: string;
};

type AmoCrmOAuthMessages = {
  popupClosed: string;
  popupTimeout: string;
  invalidStartResponse: string;
  missingReferer: string;
  exchangeFailed: string;
  oauthFailed: string;
};

const POPUP_FEATURES = 'width=600,height=720,resizable=yes,scrollbars=yes';
const POPUP_NAME = 'gochat_amocrm_oauth';
const POPUP_TIMEOUT_MS = 10 * 60 * 1000;
const AMOCRM_HOST_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.amocrm\.(ru|com)$/i;
const AMOCRM_SUBDOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

export function openAmoCrmOAuthPopup(): Window | null {
  return window.open('about:blank', POPUP_NAME, POPUP_FEATURES);
}

export async function startAmoCrmOAuth(): Promise<Required<AmoCrmOAuthStartResponse>> {
  const { data } = await integrationsInstance.post<AmoCrmOAuthStartResponse>(
    '/api/v1/oauth/amocrm/start',
    {}
  );
  if (!data.state || !data.authUrl) {
    throw new Error('oauth_start_invalid_response');
  }
  return { state: data.state, authUrl: data.authUrl };
}

export async function exchangeAmoCrmOAuth(data: {
  code: string;
  state: string;
  referer: string;
}): Promise<AmoCrmOAuthResult> {
  const response = await integrationsInstance.post<AmoCrmOAuthExchangeResponse>(
    '/api/v1/oauth/amocrm/exchange',
    data
  );
  if (!response.data.integra_id) {
    throw new Error('oauth_exchange_invalid_response');
  }
  return { integra_id: response.data.integra_id };
}

export function isAmoCrmOAuthCodeOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    if (url.protocol !== 'https:') return false;
    const host = url.hostname.toLowerCase();
    return host === 'www.amocrm.ru' || host === 'www.amocrm.com' || AMOCRM_HOST_RE.test(host);
  } catch {
    return false;
  }
}

export function isOAuthDoneOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    const host = url.hostname.toLowerCase();
    if (url.protocol === 'http:' && (host === 'localhost' || host === '127.0.0.1')) {
      return true;
    }
    if (url.protocol !== 'https:') return false;
    return (
      host === 'letsgochat.ru' ||
      host.endsWith('.letsgochat.ru') ||
      host === 'api.app.gointegration.ru' ||
      host.endsWith('.up.railway.app') ||
      isAmoCrmOAuthCodeOrigin(origin)
    );
  } catch {
    return false;
  }
}

export function resolveAmoCrmOAuthReferer(data: Record<string, unknown>): string | undefined {
  const referer = normalizeAmoCrmHost(data.referer);
  if (referer) return referer;

  const subdomain = normalizeAmoCrmSubdomain(data.subdomain);
  return subdomain ? `${subdomain}.amocrm.ru` : undefined;
}

export async function runAmoCrmOAuth(
  popup: Window,
  messages: AmoCrmOAuthMessages
): Promise<AmoCrmOAuthResult> {
  let start: Required<AmoCrmOAuthStartResponse>;
  try {
    start = await startAmoCrmOAuth();
  } catch {
    closePopup(popup);
    throw new Error(messages.invalidStartResponse);
  }

  if (!start.state || !start.authUrl) {
    closePopup(popup);
    throw new Error(messages.invalidStartResponse);
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    let guard: number | undefined;
    let timeout: number | undefined;

    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      if (guard !== undefined) window.clearInterval(guard);
      if (timeout !== undefined) window.clearTimeout(timeout);
    };

    const fail = (message: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      closePopup(popup);
      reject(new Error(message));
    };

    const succeed = (result: AmoCrmOAuthResult) => {
      if (settled) return;
      settled = true;
      cleanup();
      closePopup(popup);
      resolve(result);
    };

    async function handleMessage(event: MessageEvent) {
      const data = parseOAuthMessageData(event.data);
      if (!data) return;

      if (data.type === 'oauth_done') {
        if (!isOAuthDoneOrigin(event.origin)) return;
        if (data.status === 'ok' && typeof data.integra_id === 'string') {
          succeed({ integra_id: data.integra_id });
        } else {
          fail(typeof data.reason === 'string' ? data.reason : messages.oauthFailed);
        }
        return;
      }

      if (typeof data.code !== 'string' || !data.code) return;
      if (!isAmoCrmOAuthCodeOrigin(event.origin)) return;

      const referer = resolveAmoCrmOAuthReferer(data);
      if (!referer) {
        fail(messages.missingReferer);
        return;
      }

      if (settled) return;
      settled = true;
      cleanup();
      closePopup(popup);

      try {
        resolve(await exchangeAmoCrmOAuth({
          code: data.code,
          state: start.state,
          referer,
        }));
      } catch {
        reject(new Error(messages.exchangeFailed));
      }
    }

    window.addEventListener('message', handleMessage);

    try {
      popup.location.href = start.authUrl;
    } catch {
      fail(messages.oauthFailed);
      return;
    }

    guard = window.setInterval(() => {
      if (popup.closed) {
        fail(messages.popupClosed);
      }
    }, 500);
    timeout = window.setTimeout(() => fail(messages.popupTimeout), POPUP_TIMEOUT_MS);
  });
}

function closePopup(popup: Window) {
  try {
    popup.close();
  } catch {
    // noop
  }
}

function parseOAuthMessageData(data: unknown): Record<string, unknown> | null {
  return typeof data === 'object' && data !== null
    ? data as Record<string, unknown>
    : null;
}

function normalizeAmoCrmHost(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const raw = value.trim().toLowerCase();
  if (!raw) return undefined;

  let host = raw;
  try {
    host = raw.includes('://') ? new URL(raw).hostname.toLowerCase() : raw;
  } catch {
    return undefined;
  }

  return AMOCRM_HOST_RE.test(host) ? host : undefined;
}

function normalizeAmoCrmSubdomain(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const subdomain = value.trim().toLowerCase();
  return AMOCRM_SUBDOMAIN_RE.test(subdomain) ? subdomain : undefined;
}
